import { FC, PropsWithChildren, ReactElement } from 'react'
import { PageTransition } from 'next-page-transitions'
import { useRouter } from 'next/router'
import { observer } from 'mobx-react'
import classNames from 'classnames'
import { ToastManager } from '@components-v2/organisms/ToastManager'
import { SessionPageTemplate } from '@components/templates/SessionPageTemplate'
import { GlobalHeader } from '@components-v2/organisms/GlobalHeader'
import { PageTransitionInner } from '@components/organisms/PageTransitionInner'
import { BackButtonBar } from '@components/molecules/BackButtonBar'
import styles from './index.module.scss'

type Props = {
  renderTabBar: () => ReactElement
  renderPageHeader: () => ReactElement
  renderContentHeader?: () => ReactElement
  renderSideBar?: () => ReactElement
  renderContentTabBar?: () => ReactElement
  backButtonLabel?: string
  backButtonHref?: string
  containerType?: 'default' | 'wideContainer'
  visiblePageHeader?: boolean
  noSpaceOnContentTabBarSideForMobile?: boolean
}

export const SettingsPageTemplate: FC<PropsWithChildren<Props>> = observer(
  ({
    renderPageHeader,
    renderTabBar,
    renderContentHeader,
    renderSideBar,
    renderContentTabBar,
    backButtonLabel,
    backButtonHref = '/',
    containerType = 'default',
    visiblePageHeader = false,
    noSpaceOnContentTabBarSideForMobile = false,
    children,
  }) => {
    const router = useRouter()

    const pageHeaderClassName = classNames({
      [styles.pageHeader]: !visiblePageHeader,
      [styles.pageHeaderVisibility]: visiblePageHeader,
    })
    const pageHeaderInnerClassName = classNames({
      [styles.pageHeaderInner]: containerType === 'default',
      [styles.pageHeaderInnerWide]: containerType === 'wideContainer',
    })
    const contentClassName = classNames(
      {
        [styles.content]: containerType === 'default',
      },
      {
        [styles.isSingleColumnContent]: !renderSideBar,
        [styles.isDoubleColumnContent]: renderSideBar,
      },
      {
        [styles.hasContentTabBar]: renderContentTabBar,
      }
    )

    const contentTabBarClassName = classNames({
      [styles.contentTabBar]: containerType === 'default',
      [styles.contentTabBarWide]: containerType === 'wideContainer',
      [styles.contentTabBarNoSpaceOnSideForMobile]: noSpaceOnContentTabBarSideForMobile,
    })

    const mainContentClassName = classNames({
      [styles.isSingleColumnMainContent]: !renderSideBar,
      [styles.isDoubleColumnMainContent]: renderSideBar,
    })
    return (
      <SessionPageTemplate>
        <div className={styles.container}>
          <GlobalHeader isFullWidth />
          <div className={styles.tabBar}>{renderTabBar()}</div>
          <div className={styles.contentContainer}>
            <PageTransition timeout={300} classNames='page-transition'>
              <PageTransitionInner key={router.route}>
                {backButtonLabel && (
                  <div className={styles.backButtonBar}>
                    <BackButtonBar href={backButtonHref} label={backButtonLabel} />
                  </div>
                )}
                <div className={pageHeaderClassName}>
                  <div className={pageHeaderInnerClassName}>{renderPageHeader()}</div>
                </div>
                <div className={contentClassName}>
                  {renderContentTabBar && <div className={contentTabBarClassName}>{renderContentTabBar()}</div>}
                  {renderSideBar && <div className={styles.sidebar}>{renderSideBar()}</div>}
                  <main className={mainContentClassName}>
                    {renderContentHeader && <div className={styles.contentHeader}>{renderContentHeader()}</div>}
                    <div className={styles.contentBody}>{children}</div>
                  </main>
                </div>
              </PageTransitionInner>
            </PageTransition>
          </div>
          <ToastManager />
        </div>
      </SessionPageTemplate>
    )
  }
)
